import { BCP47Schema } from "./enums";

/**
 * The signup query parameter a trial link carries the marketing page's locale
 * in, beside `SIGNUP_REGIME_PARAM`. The marketing site keeps no language
 * cookie and the app is on another origin, so the link is the only thing that
 * carries the reader's language to signup.
 */
export const SIGNUP_LANGUAGE_PARAM = "lang";

/**
 * The key the app sends the signup language under in a sign-up request's
 * `additionalData`, which better-auth also carries through an OAuth round trip
 * in its state, so one key serves the email form and both social providers.
 */
export const SIGNUP_LANGUAGE_DATA_KEY = "signupLanguage";

/** A bare ISO 639 language subtag: two letters, or three for the rest. */
const LANGUAGE_SUBTAG_RE = /^[a-z]{2,3}$/u;

/**
 * ISO 639's codes that parse as a language and name none: undetermined, no
 * linguistic content, multiple languages, uncoded languages.
 */
const NON_LANGUAGE_CODES = new Set(["und", "zxx", "mul", "mis"]);

/** ISO 639's private-use range, `qaa` to `qtz`, which names nothing shared. */
const PRIVATE_USE_RE = /^q[a-t][a-z]$/u;

/**
 * The language a signup arrived in, as its bare subtag (`sl`, `it`), or null
 * when the value names none.
 *
 * The value comes off a URL a reader can edit, so anything that is not a
 * well-formed tag is dropped rather than recorded. A well-formed one is kept
 * whether or not the app speaks it yet: the record says which page the host
 * read, and a language the app adds later can use it from then on.
 */
export function signupLanguageFrom(value: unknown): string | null {
  const tag = BCP47Schema.safeParse(value);
  if (!tag.success) {
    return null;
  }
  const { language } = new Intl.Locale(tag.data);
  const namesALanguage =
    LANGUAGE_SUBTAG_RE.test(language) &&
    !NON_LANGUAGE_CODES.has(language) &&
    !PRIVATE_USE_RE.test(language);
  return namesALanguage ? language : null;
}
